import { nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../lib/shigo-api-client';
import {
  namedTargetingEntitySchema,
  targetingDeviceTypeSchema,
  targetingIabCategoriesSchema,
  targetingLanguageSchema,
} from './apiSchemas';
import {
  TargetingCsvKind,
  TargetingPlatformCatalog,
  type GeolocationOption,
  type LiverampSegment,
  type NamedTargetingEntity,
  type TargetingDeviceType,
  type TargetingIabCategory,
  type TargetingLanguage,
  type TargetingPlacement,
} from './model';

type TargetingClientOptions = {
  config: ShigoApiConfig;
  rootApiConfig?: ShigoApiConfig | undefined;
  agencyId?: string | undefined;
  accessToken: string;
  fetcher?: typeof fetch;
};

const geolocationOptionSchema = z
  .looseObject({
    cityId: nullableOptional(z.string()),
    cityName: nullableOptional(z.string()),
    code: nullableOptional(z.string()),
    continentCode: nullableOptional(z.string()),
    continentName: nullableOptional(z.string()),
    countryIsoCode: nullableOptional(z.string()),
    countryName: nullableOptional(z.string()),
    geonameId: nullableOptional(z.union([z.string(), z.number()]).transform(String)),
    isInEuropeanUnion: nullableOptional(z.boolean()),
    metroCode: nullableOptional(z.string()),
    name: nullableOptional(z.string()),
    subdivision1IsoCode: nullableOptional(z.string()),
    subdivision1Name: nullableOptional(z.string()),
    subdivision2IsoCode: nullableOptional(z.string()),
    subdivision2Name: nullableOptional(z.string()),
    timeZone: nullableOptional(z.string()),
  })
  .transform((location) => ({
    ...location,
    name:
      location.name ??
      location.cityName ??
      location.subdivision2Name ??
      location.subdivision1Name ??
      location.countryName ??
      location.continentName ??
      location.code ??
      location.geonameId ??
      'Unknown location',
  }));

const geolocationOptionsSchema = z.array(geolocationOptionSchema);
const liverampSegmentSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  segmentId: z.union([z.string(), z.number()]).transform(String).optional(),
  segmentName: z.string().optional().default(''),
  providerName: z.string().optional().default(''),
  description: z.string().nullish(),
  segmentDescription: z.string().nullish(),
  useRestrictions: z.string().nullish(),
  permittedAdvertisers: z.string().nullish(),
  customSegment: z.boolean().optional(),
  digitalCpm: z.number().nullish(),
  tvCpm: z.number().nullish(),
});
const liverampSegmentsPageSchema = z.looseObject({
  content: z.array(liverampSegmentSchema).optional().default([]),
  totalElements: z.number().int().nonnegative().optional().default(0),
});
const targetingCsvImportSchema = z.union([
  z.number().int().nonnegative(),
  z.looseObject({ count: z.number().int().nonnegative() }).transform((response) => response.count),
]);
const targetingPlacementSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  secondaryId: z.string().nullish(),
  deliveryType: z.string().nullish(),
  bidFloor: z.number().nullish(),
  cpm: z.number().nullish(),
});
const targetingPlacementsPageSchema = z.looseObject({
  content: z.array(targetingPlacementSchema).optional().default([]),
  totalElements: z.number().int().nonnegative().optional().default(0),
});
const targetingPublisherSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  secondaryId: z.string().nullish(),
});
const targetingPublishersPageSchema = z.looseObject({
  content: z.array(targetingPublisherSchema).optional().default([]),
  totalElements: z.number().int().nonnegative().optional().default(0),
});

export type TargetingPublisher = z.infer<typeof targetingPublisherSchema>;

export function createTargetingClient({
  config,
  rootApiConfig,
  agencyId,
  accessToken,
  fetcher,
}: TargetingClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  const rootApi = createShigoApiClient(rootApiConfig ?? config, accessToken, fetcher);
  const csvApi = rootApiConfig ? rootApi : api;
  const platformCatalog = rootApiConfig
    ? TargetingPlatformCatalog.Shigo
    : TargetingPlatformCatalog.MadBuy;
  const requestLocations = (path: string): Promise<GeolocationOption[]> =>
    api.request({ path, schema: geolocationOptionsSchema });

  return {
    platformCatalog,
    ...({ queryScope: agencyId ? [config.networkId, agencyId] : [] } as {
      queryScope?: readonly string[];
    }),
    listDeviceTypes: (): Promise<TargetingDeviceType[]> =>
      api.request({
        path: '/targetings/geo/device-types',
        schema: targetingDeviceTypeSchema.array(),
      }),
    listPlatforms: (): Promise<NamedTargetingEntity[]> =>
      (rootApiConfig ? rootApi : api).request({
        path: rootApiConfig ? '/api/apps/platforms' : '/apps/platforms',
        schema: namedTargetingEntitySchema.array(),
      }),
    listIabCategories: (
      params: Record<string, string | number> = {},
    ): Promise<TargetingIabCategory[]> => {
      const search = toSearchParams(params).toString();
      return api.request({
        path: `/iab-categories${search ? `?${search}` : ''}`,
        schema: targetingIabCategoriesSchema,
      });
    },
    listLanguages: (): Promise<TargetingLanguage[]> =>
      api.request({
        path: '/targetings/geolocations/languages',
        schema: targetingLanguageSchema.array(),
      }),
    listSupplyCompanies: (): Promise<NamedTargetingEntity[]> =>
      api.request({
        path: '/company-settings/supply-companies',
        schema: namedTargetingEntitySchema.array(),
      }),
    listContinents: () => requestLocations('/targetings/geolocations/continents'),
    listCountries: (params: Record<string, string | number> = {}) =>
      requestLocations(`/targetings/geolocations/countries?${toSearchParams(params)}`),
    listRegions: (params: Record<string, string | number>) =>
      requestLocations(`/targetings/geolocations/regions?${toSearchParams(params)}`),
    listSubregions: (params: Record<string, string | number>) =>
      requestLocations(`/targetings/geolocations/subregions?${toSearchParams(params)}`),
    listCities: (params: Record<string, string | number>) =>
      requestLocations(`/targetings/geolocations/cities?${toSearchParams(params)}`),
    searchLocations: (params: Record<string, string | number>) =>
      requestLocations(`/targetings/geolocations/search?${toSearchParams(params)}`),
    listLiverampSegments: ({
      page = 0,
      size = 5,
      searchTerm = '',
      providerName,
      segmentName,
      segmentIds,
    }: {
      page?: number;
      size?: number;
      searchTerm?: string;
      providerName?: string;
      segmentName?: string;
      segmentIds?: string[];
    }): Promise<{ content: LiverampSegment[]; totalElements: number }> => {
      const normalizedSearch = searchTerm.trim();
      const params = new URLSearchParams({
        page: String(page),
        size: String(segmentIds?.length ? segmentIds.length : size),
        providerName: providerName?.trim() ?? normalizedSearch,
        segmentName: segmentName?.trim() ?? normalizedSearch,
        sortProperty: 'segmentName',
        sortDirection: 'ASC',
      });
      if (segmentIds?.length) params.set('segmentIds', segmentIds.join(','));
      return rootApi.request({
        path: `/api/lr/segments?${params.toString()}`,
        schema: liverampSegmentsPageSchema,
      });
    },
    listPlacements: ({
      searchTerm = '',
      page = 0,
      size = 10,
      ids,
      secondaryIds,
    }: {
      searchTerm?: string;
      page?: number;
      size?: number;
      ids?: string[];
      secondaryIds?: string[];
    }): Promise<{ content: TargetingPlacement[]; totalElements: number }> => {
      const params = new URLSearchParams();
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch) params.set('searchTerm', trimmedSearch);
      params.set('status', 'ACTIVE');
      params.set('page', String(page));
      params.set('size', String(ids?.length || secondaryIds?.length || size));
      params.set('sortProperty', 'name');
      params.set('sortDirection', 'ASC');
      if (ids?.length) params.set('ids', ids.join(','));
      if (secondaryIds?.length) params.set('secondaryIds', secondaryIds.join(','));
      return rootApi.request({
        path: `/api/placements?${params.toString()}`,
        schema: targetingPlacementsPageSchema,
      });
    },
    listPublishers: ({
      searchTerm = '',
      page = 0,
      size = 10,
      ids,
      secondaryIds,
    }: {
      searchTerm?: string;
      page?: number;
      size?: number;
      ids?: string[];
      secondaryIds?: string[];
    }): Promise<{ content: TargetingPublisher[]; totalElements: number }> => {
      const params = new URLSearchParams({
        status: 'ACTIVE',
        page: String(page),
        size: String(ids?.length || secondaryIds?.length || size),
        sortProperty: 'name',
        sortDirection: 'ASC',
      });
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch) params.set('name', trimmedSearch);
      if (ids?.length) params.set('ids', ids.join(','));
      if (secondaryIds?.length) params.set('secondaryIds', secondaryIds.join(','));
      return rootApi.request({
        path: `/api/publishers?${params.toString()}`,
        schema: targetingPublishersPageSchema,
      });
    },
    importBundlesCsv: (targetingId: string, file: File) =>
      importTargetingCsv(csvApi, targetingId, TargetingCsvKind.Bundles, file),
    exportBundlesCsv: (targetingId: string) =>
      csvApi.requestBlob({
        path: `/api/targetings/${encodeURIComponent(targetingId)}/csv/export-bundles`,
      }),
    importIpsCsv: (targetingId: string, file: File) =>
      importTargetingCsv(csvApi, targetingId, TargetingCsvKind.Ips, file),
    exportIpsCsv: (targetingId: string) =>
      csvApi.requestBlob({
        path: `/api/targetings/${encodeURIComponent(targetingId)}/csv/export-ips`,
      }),
  };
}

function importTargetingCsv(
  api: ReturnType<typeof createShigoApiClient>,
  targetingId: string,
  kind: TargetingCsvKind,
  file: File,
) {
  const body = new FormData();
  body.append('file', file);
  return api.request({
    path: `/api/targetings/${encodeURIComponent(targetingId)}/csv/import-${kind}`,
    schema: targetingCsvImportSchema,
    init: { method: 'POST', body },
  });
}

export type TargetingClient = ReturnType<typeof createTargetingClient>;

export type GeolocationTargetingClient = Pick<
  TargetingClient,
  | 'queryScope'
  | 'listContinents'
  | 'listCountries'
  | 'listRegions'
  | 'listSubregions'
  | 'listCities'
  | 'searchLocations'
>;
